/**
 * cleanupCache — prune orphaned localStorage entries for deleted designs
 * and projects. Called once per page mount (NewChat / DesignView) after
 * the first listDesigns resolves so we have the authoritative set of live
 * IDs.
 *
 * Removed entries:
 *   - canvas_<designId> (BlockNote editor state)
 *   - ajo:blocks:<designId> (legacy serialized blocks cache)
 *   - project-tree-expanded / project-tree-collapsed (prune missing project IDs from JSON maps)
 *
 * Preserved entries:
 *   - ajo.colorMode (app-level UI state)
 *   - design-leftrail-collapsed (app-level UI state)
 *   - design-panel-mode (app-level UI state)
 *   - ajo-designs-snapshot:* (SWR cache, swept separately on logout)
 */
export function cleanupOrphanedCache(
  currentDesignIds: string[],
  currentProjectIds: string[],
): void {
  const designSet = new Set(currentDesignIds);
  const projectSet = new Set(currentProjectIds);

  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      // Canvas editor state: canvas_<designId>
      if (key.startsWith('canvas_')) {
        const designId = key.slice('canvas_'.length);
        if (designId && !designSet.has(designId)) {
          localStorage.removeItem(key);
        }
      }
      // Legacy blocks cache: ajo:blocks:<designId>
      if (key.startsWith('ajo:blocks:')) {
        const designId = key.slice('ajo:blocks:'.length);
        if (designId && !designSet.has(designId)) {
          localStorage.removeItem(key);
        }
      }
    }

    // Project tree expanded/collapsed maps — parse, prune missing IDs, write back
    for (const mapKey of ['project-tree-expanded', 'project-tree-collapsed']) {
      try {
        const raw = localStorage.getItem(mapKey);
        if (!raw) continue;
        const map = JSON.parse(raw);
        if (typeof map !== 'object' || !map) continue;
        let changed = false;
        for (const pid of Object.keys(map)) {
          if (!projectSet.has(pid)) {
            delete map[pid];
            changed = true;
          }
        }
        if (changed) {
          localStorage.setItem(mapKey, JSON.stringify(map));
        }
      } catch {
        // parse error or quota error — silently skip
      }
    }
  } catch {
    // private mode, quota, or other localStorage failure — silently skip
  }
}
